import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowRight, FaSearch, FaTimes } from "react-icons/fa";

const flattenMenu = (sections = []) => {
  const rows = [];

  const addItems = (items, section, parent = "") => {
    (items || []).forEach((item) => {
      const title = item?.title || item?.name || "";
      const breadcrumb = [parent, title].filter(Boolean).join(" / ");

      if (item?.link || item?.path) {
        rows.push({
          section,
          title,
          breadcrumb,
          link: item.link || item.path,
        });
      }

      if (Array.isArray(item?.children)) {
        addItems(item.children, section, breadcrumb);
      }
    });
  };

  sections.forEach((section) => {
    addItems(section.items, section.title);
  });

  return rows;
};

const GlobalMenuNavigator = ({ sections = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const keyboardNavigationRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const allMenus = useMemo(() => flattenMenu(sections), [sections]);
  const filteredMenus = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return allMenus;

    return allMenus.filter((item) =>
      `${item.section} ${item.breadcrumb} ${item.title}`
        .toLowerCase()
        .includes(query),
    );
  }, [allMenus, search]);

  const close = () => {
    setOpen(false);
    setSearch("");
    setActiveIndex(0);
  };

  const openMenu = () => {
    setOpen(true);
    setSearch("");
    setActiveIndex(0);
  };

  const goTo = (item) => {
    if (!item?.link) return;
    close();
    if (location.pathname !== item.link) navigate(item.link);
  };

  useEffect(() => {
    const onGlobalKeyDown = (event) => {
      if (
        open &&
        (event.ctrlKey || event.metaKey) &&
        event.key === "Backspace"
      ) {
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "m") {
        event.preventDefault();
        event.stopPropagation();
        if (open) close();
        else openMenu();
        return;
      }

      if (!open) return;

      if (event.key === "Escape") {
        event.preventDefault();
        close();
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        keyboardNavigationRef.current = true;
        setActiveIndex((index) =>
          filteredMenus.length ? (index + 1) % filteredMenus.length : 0,
        );
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        keyboardNavigationRef.current = true;
        setActiveIndex((index) =>
          filteredMenus.length
            ? (index - 1 + filteredMenus.length) % filteredMenus.length
            : 0,
        );
      } else if (event.key === "Enter" && filteredMenus[activeIndex]) {
        event.preventDefault();
        goTo(filteredMenus[activeIndex]);
      }
    };

    window.addEventListener("keydown", onGlobalKeyDown, true);
    return () => window.removeEventListener("keydown", onGlobalKeyDown, true);
  }, [open, filteredMenus, activeIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const focusId = requestAnimationFrame(() => inputRef.current?.focus());
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      cancelAnimationFrame(focusId);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
    keyboardNavigationRef.current = false;
  }, [search]);

  useEffect(() => {
    if (!open || !keyboardNavigationRef.current) return;
    document
      .querySelector(`[data-global-menu-index="${activeIndex}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    keyboardNavigationRef.current = false;
  }, [activeIndex, open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[10000] flex items-start justify-center bg-slate-950/45 px-3 pt-[10vh] backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: -18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ duration: 0.16 }}
            className="w-full max-w-2xl overflow-hidden rounded-2xl border border-white/60 bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Navigate to menu"
          >
            <div className="flex items-center gap-3 border-b border-slate-200 bg-gradient-to-r from-[#9481ce] to-[#7662bd] px-4 py-3 text-white">
              <div>
                <h2 className="text-sm font-semibold">Go to Menu</h2>
                <p className="text-[11px] text-violet-100">
                  Search a menu and press Enter to open it
                </p>
              </div>
              <span className="ml-auto rounded-lg bg-white/15 px-2 py-1 text-[10px] font-semibold">
                Ctrl + M
              </span>
              <button
                type="button"
                onClick={close}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-white hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/60"
                aria-label="Close menu search"
              >
                <FaTimes />
              </button>
            </div>

            <div className="border-b border-slate-200 p-3">
              <div className="relative">
                <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400" />
                <input
                  ref={inputRef}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search sales, customer, stock, reports..."
                  className="w-full rounded-xl border border-slate-300 bg-slate-50 py-2.5 pl-9 pr-3 text-sm text-slate-800 outline-none transition focus:border-[#9481ce] focus:bg-white focus:ring-4 focus:ring-[#9481ce]/15"
                  aria-label="Search menus"
                />
              </div>
            </div>

            <div className="max-h-[55vh] scroll-smooth overflow-y-auto overscroll-contain p-2 [scrollbar-gutter:stable]">
              {filteredMenus.length ? (
                filteredMenus.map((item, index) => {
                  const active = index === activeIndex;
                  const current = location.pathname === item.link;
                  return (
                    <button
                      key={`${item.section}-${item.link}`}
                      type="button"
                      data-global-menu-index={index}
                      onMouseEnter={() => {
                        keyboardNavigationRef.current = false;
                        setActiveIndex(index);
                      }}
                      onClick={() => goTo(item)}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
                        active
                          ? "bg-violet-100 text-[#5d49a7]"
                          : "text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <span
                        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${
                          active
                            ? "bg-[#9481ce] text-white"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {item.title.charAt(0)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="truncate text-sm font-semibold">
                            {item.title}
                          </span>
                          <span
                            className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                              item.section === "Reports"
                                ? "bg-amber-100 text-amber-700"
                                : item.section === "Stocks"
                                  ? "bg-sky-100 text-sky-700"
                                  : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {item.section}
                          </span>
                        </span>
                        <span className="block truncate text-[11px] text-slate-500">
                          {item.breadcrumb}
                        </span>
                      </span>
                      {current && (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                          Current
                        </span>
                      )}
                      <FaArrowRight className="shrink-0 text-xs opacity-60" />
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-10 text-center">
                  <p className="text-sm font-semibold text-slate-600">
                    No menu found
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    Try a different search term.
                  </p>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 bg-slate-50 px-4 py-2 text-[10px] text-slate-500">
              <span>↑ ↓ Navigate</span>
              <span>Enter Open</span>
              <span>Esc Close</span>
              <span className="ml-auto">{filteredMenus.length} menus</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GlobalMenuNavigator;
